import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import imageCompression from 'browser-image-compression';
import { useMobileConnection } from 'context/SignalR/mobileActionsConnection';
import { ON } from 'enums/signalrConnection';
import { useLoading } from 'hooks/useLoading';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const FileUploadBox = (props) => {
    const { onUpload, avatar } = props;
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const dropRef = useRef(null);
    const fileInputRef = useRef(null);
    const [dragCounter, setDragCounter] = useState();
    const [dragging, setDragging] = useState(false);

    const { setLoading } = useLoading();

    const { connection } = useMobileConnection();

    /* const handleDataReceived = (data) => {
        console.log(data);
    }; */

    /* useEffect(() => {
        if (connection && connection.state === 'Connected') {
            connection.on(ON.actionResponse, handleDataReceived);
        }
    }, [connection]); */

    const handleUpload = (files) => {
        if (onUpload && typeof onUpload === 'function') onUpload(files);
    };

    const handleCompressFiles = async (files) => {
        const compressedFiles = [];
        if (files && files.length > 0) {
            /* eslint-disable */
            setLoading(true);
            for (const file of files) {
                try {
                    const compression = await imageCompression(file, {
                        maxSizeMB: 2,
                        maxWidthOrHeight: 1024,
                        useWebWorker: true
                    });
                    const compressedFile = new File([compression], file?.name, { type: file?.type });
                    compressedFiles.push(compressedFile);
                } catch {
                    setLoading(false);
                    enqueueSnackbar(`${file.name} ${t('FileUploadError')}`, { variant: 'error' });
                }
            }
            setLoading(false);
        }
        return compressedFiles;
    };

    const handleCheckFiles = async (files) => {
        const filesToUpload = [];
        const filesToCompress = [];
        const filesToEnqueue = [];
        if (files && files.length > 0) {
            files.forEach((file) => {
                if (!file.type.startsWith('image')) {
                    if (avatar) filesToEnqueue.push(`${file.name} ${t('FileNotImage')}`);
                    else if (file.size > 2097152) filesToEnqueue.push(`${file.name} ${t('FileTooLarge')}`); // 2 * 1024 * 1024 = 2MB
                    else filesToUpload.push(file);
                } else if (file.size > 2097152) filesToCompress.push(file); // 2 * 1024 * 1024 = 2MB
                else filesToUpload.push(file);
            });
        }
        if (filesToCompress.length > 0) {
            const compressedFiles = await handleCompressFiles(filesToCompress);
            if (compressedFiles && compressedFiles.length > 0) filesToUpload.push(...compressedFiles);
        }

        if (filesToEnqueue.length > 0) enqueueSnackbar(filesToEnqueue.join('\n'), { variant: 'error' });

        if (filesToUpload.length > 0) handleUpload(filesToUpload);
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragCounter((prev) => prev + 1);
        setDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragCounter((prev) => prev - 1);
    };

    useEffect(() => {
        if (dragCounter === 0) {
            setDragging(false);
        }
    }, [dragCounter]);

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragging(false);
        const files = [...event.dataTransfer.files];
        handleCheckFiles(files);
    };

    useEffect(() => {
        const instance = dropRef.current;
        if (instance) {
            setDragCounter(0);
            instance.addEventListener('dragover', handleDragOver);
            instance.addEventListener('drop', handleDrop);
            instance.addEventListener('dragenter', handleDragEnter);
            instance.addEventListener('dragleave', handleDragLeave);
        }

        return () => {
            if (instance) {
                instance.removeEventListener('dragover', handleDragOver);
                instance.removeEventListener('drop', handleDrop);
                instance.removeEventListener('dragenter', handleDragEnter);
                instance.removeEventListener('dragleave', handleDragLeave);
            }
        };
    }, []);

    const handleOpenFileExplorer = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <Box
            ref={dropRef}
            sx={{
                display: 'flex',
                borderWidth: '3px',
                borderStyle: 'dashed',
                borderColor: dragging ? '#34eb4c' : 'grey',
                backgroundColor: dragging ? '#ccfcd3' : '#f3f3f4',
                borderRadius: 4,
                flexGrow: 1,
                m: 1
            }}
            onMouseEnter={() => {
                setDragging(false);
                setDragCounter(0);
            }}
            onMouseLeave={() => {
                setDragging(false);
                setDragCounter(0);
            }}
            justifyContent="center"
            alignItems="center"
        >
            {!dragging && (
                <Stack spacing={2} alignItems="center">
                    <FileUploadOutlinedIcon sx={{ fontSize: 180, color: '#4d79bc' }} />
                    <Typography sx={{ fontSize: 30 }}>{t('DragToUpload')}</Typography>
                    <Typography sx={{ fontSize: 17 }}>{t('Or')}</Typography>
                    <Button
                        variant="contained"
                        sx={{ background: '#4d79bc', ':hover': { background: '#79AEC9', color: '#fff' } }}
                        onClick={handleOpenFileExplorer}
                    >
                        <Typography>{t('OpenFileExplorer')}</Typography>
                    </Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept={avatar ? 'image/*' : '*'}
                        style={{ display: 'none' }}
                        multiple="multiple"
                        onChange={async (event) => {
                            const files = [...event.target.files];
                            await handleCheckFiles(files);
                        }}
                    />
                </Stack>
            )}
            {dragging && (
                <Stack spacing={2} alignItems="center">
                    <GetAppOutlinedIcon sx={{ fontSize: 180, color: '#34eb4c' }} />
                    <Typography sx={{ fontSize: 30 }}>{t('DropToUpload')}</Typography>
                </Stack>
            )}
        </Box>
    );
};

export default FileUploadBox;
